import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Snackbar,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import HttpService from "../services/HttpService";
import CloseIcon from "@mui/icons-material/Close";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import AddCompanyGroup from "./AddCompanyGroup";
import { useState } from "react";
import AddMemberToGroup from "./AddMemberToGroup";
import Labels from "../labels/Labels";

export default function ManageGroupMembers(props) {
  const [feedback, setFeedback] = useState({
    open: false,
    type: "error",
    message: "",
  });

  const queryClient = useQueryClient();

  async function fetchGroupMembers() {
    const response = await HttpService.getAxiosClient().get(
      window.API_URL + `/companyGroups/${props?.groupInfo?.id}/members`
    );
    return response;
  }

  const deleteGroupMemberMutation = useMutation(
    (values) => {
      return HttpService.getAxiosClient().delete(
        window.API_URL + `/companyGroups/member/${values.id}`
      );
    },
    {
      onSuccess: (dataResponse, value) => {
        queryClient.invalidateQueries(["memberGroups", props?.groupInfo?.id]);
        queryClient.invalidateQueries("companyGroups");
      },
      onError: (error) => {
        setFeedback({
          open: true,
          type: "error",
          message: Labels.getLabel(
            "manageGroupMembers.errorDeletingAttribute",
            "Error deleting the attribute"
          ),
        });
      },
    }
  );

  const columns = [
    { field: "id", headerName: "id", width: 20, hide: true },
    {
      field: "memberId",
      headerName: Labels.getLabel(
        "manageGroupMembers.memberIdColumn",
        "Member ID"
      ),
      width: 80,
    },
    {
      field: "email",
      headerName: Labels.getLabel("manageGroupMembers.emailColumn", "Email"),
      width: 350,
    },
    {
      field: "name",
      headerName: Labels.getLabel("manageGroupMembers.nameColumn", "Name"),
      width: 350,
    },
    {
      field: "action",
      headerName: Labels.getLabel("manageGroupMembers.actionColumn", "Action"),
      width: 50,
      renderCell: (params) => (
        <div key={"action." + params.id}>
          <IconButton
            key={"delete." + params.id}
            onClick={() => {
              deleteGroupMemberMutation.mutate({ ...params });
            }}
          >
            <PersonRemoveIcon />
          </IconButton>
        </div>
      ),
    },
  ];

  const { data, isLoading } = useQuery(
    ["memberGroups", props?.groupInfo?.id],
    fetchGroupMembers
  );
  const [addMemberDlg, setAddMemberDlg] = useState(false);

  const addMemberDlgFunct = (event) => {
    return (
      <Dialog open={addMemberDlg} fullWidth onClose={() => {}}>
        <Box
          sx={{
            backgroundColor: "primary.main",
            px: 3,
            minHeight: 52,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <PersonAddIcon sx={{ color: "white" }} />
            <Typography variant="h6" sx={{ color: "white" }}>
              {Labels.getLabel(
                "manageGroupMembers.addMemberTitle",
                "Add Member to Group"
              )}
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              setAddMemberDlg(false);
              queryClient.invalidateQueries([
                "memberGroups",
                props?.groupInfo?.id,
              ]);
              queryClient.invalidateQueries("companyGroups");
            }}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ paddingTop: 3 }}>
          <AddMemberToGroup groupInfo={props?.groupInfo} />
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      {isLoading ? (
        ""
      ) : (
        <Grid container p={0} m={0} spacing={0}>
          <Grid item xs={12} sx={{ margin: "0px 0px 10px 0px" }}>
            <Box display="flex">
              <Typography variant="h5">
                {Labels.getLabel("manageGroupMembers.groupPrefix", "Group:")}{" "}
                {props?.groupInfo?.name}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setAddMemberDlg(true)}
                sx={{ margin: "0px 10px 0px 10px" }}
              >
                {Labels.getLabel(
                  "manageGroupMembers.addMembersButton",
                  "Add Members"
                )}
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} sx={{ width: "100%", height: "70vh" }}>
            <DataGrid
              key={data?.data?.id}
              rows={data?.data}
              columns={columns}
              getRowHeight={() => 25}
              density="compact"
            />
          </Grid>
        </Grid>
      )}
      {addMemberDlg ? addMemberDlgFunct() : ""}
      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={() => setFeedback({ ...feedback, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setFeedback({ ...feedback, open: false })}
          severity={feedback.type}
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
